import React from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom-v5-compat';

import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { SellerFeatures } from '../../../../enums/seller-features';
import { useIsSellerFeatureEnabled } from '../../../../hooks/use-is-seller-feature-enabled';
import messages from '../../messages';
import { AddPurchaseOrderButton } from '../../purchase-orders/add-purchase-order-button';
import { useInventoryContext } from '../inventory-context';

/**
 * @module CreatePurchaseOrderButton
 * @description A button component for creating purchase orders from selected inventory items.
 * Uses the inventory context to get selected product IDs and passes them to the AddPurchaseOrderButton.
 * If inventoryWarehouseId is present in the URL params, it includes it in the initialized purchase order.
 * @returns {JSX.Element|null} The AddPurchaseOrderButton wrapped in SupplierProvider, or null if purchase orders are disabled
 */
const CreatePurchaseOrderButton = () => {
  const intl = useIntl();
  const { inventoryWarehouseId } = useParams();
  const { selectedProductIds } = useInventoryContext();
  const isInventoryReorderingEnabled = useIsSellerFeatureEnabled(SellerFeatures.InventoryReordering);

  const initializedPurchaseOrder = {
    selectedProductsWithQuantity: selectedProductIds.map((productId) => ({
      productId,
      quantity: 1,
    })),
    warehouseId: inventoryWarehouseId || null,
  };

  const text = intl.formatMessage(messages.createPurchaseOrderButton);
  const isDisabled = selectedProductIds.length === 0 || !isInventoryReorderingEnabled;

  return (
    <AddPurchaseOrderButton
      disabled={isDisabled}
      initializedPurchaseOrder={initializedPurchaseOrder}
      text={text}
      theme={THEME_CONSTANTS.tertiary}
    />
  );
};

export default CreatePurchaseOrderButton;
